import {
  type HTMLAttributes,
  type DetailedHTMLProps,
  type DOMAttributes,
  useEffect,
  useContext,
  useState,
  useCallback,
  useReducer,
  useMemo
} from 'react';
import { BrowserRouter as Router, Switch, Route, useParams, useHistory } from 'react-router-dom';
import ZoomVideo, {
  type VideoPlayerContainer,
  type VideoPlayer,
  ConnectionState,
  ReconnectReason
} from '@zoom/videosdk';
import { message, Modal } from 'antd';
import 'antd/dist/antd.min.css';
import produce from 'immer';
import Home from './feature/home/home';
import Video from './feature/video/video';
import VideoSingle from './feature/video/video-single';
import VideoAttach from './feature/video/video-attach';
import Preview from './feature/preview/preview';
import ZoomContext from './context/zoom-context';
import ZoomMediaContext from './context/media-context';
import LoadingLayer from './component/loading-layer';
import type { MediaStream } from './index-types';
import './App.css';
import { requestServerApi, serverApi } from './api/api';
import { ENABLE_DEBUG_MODE, IS_DEMO, IS_DEV } from './config/env-config';
import NotFound from './feature/error/NotFound';
import { detectDeviceType } from './utils/DeviceType';
import ChatProvider from './feature/video/context/chat-context';
import LessonProvider from './context/lesson-context';
import PiPProvider from './feature/video/context/pip-context';
import { isAndroidOrIOSBrowser } from './utils/platform';
import UserProvider from './context/user-context';
import DeviceSettingProvider from './context/device-setting-context';
import ErrorFallback from './component/ErrorFallback';
import { ErrorBoundary } from 'react-error-boundary';
import { getLocalStorage } from './utils/web-storage-utils';
import DebugModeProvider from './context/debug-mode-context';
import DebugModeBotton from './feature/debug-mode/DebugModeBtton/DebugModeBotton';
import LoggerProvider from './context/logger-context';
import useLogger from './hooks/useLogger';
import ErrorStateProvider from './context/error-state-context';
import ErrorDialog from './component/Modal/ErrorDialog';
import useErrorStateContext from './hooks/useErrorStateContext';
type CustomElement<T> = Partial<T & DOMAttributes<T> & { children: any }>;

interface AppProps {}
const mediaShape = {
  audio: {
    encode: false,
    decode: false
  },
  video: {
    encode: false,
    decode: false
  },
  share: {
    encode: false,
    decode: false
  }
};
const mediaReducer = produce((draft, action) => {
  switch (action.type) {
    case 'audio-encode': {
      draft.audio.encode = action.payload;
      break;
    }
    case 'audio-decode': {
      draft.audio.decode = action.payload;
      break;
    }
    case 'video-encode': {
      draft.video.encode = action.payload;
      break;
    }
    case 'video-decode': {
      draft.video.decode = action.payload;
      break;
    }
    case 'share-encode': {
      draft.share.encode = action.payload;
      break;
    }
    case 'share-decode': {
      draft.share.decode = action.payload;
      break;
    }
    case 'reset-media': {
      Object.assign(draft, { ...mediaShape });
      break;
    }
    default:
      break;
  }
}, mediaShape);

declare global {
  interface Window {
    webEndpoint: string | undefined;
    zmClient: any | undefined;
    mediaStream: any | undefined;
    crossOriginIsolated: boolean;
    ltClient: any | undefined;
    logClient: any | undefined;
    documentPictureInPicture: any;
  }
  namespace JSX {
    interface IntrinsicElements {
      ['video-player']: DetailedHTMLProps<HTMLAttributes<VideoPlayer>, VideoPlayer> & { class?: string };
      ['video-player-container']: CustomElement<VideoPlayerContainer> & { class?: string };
    }
  }
}

export type UrlPramsPreview = {
  roomId: string;
  companyId: string;
};

function App(props: AppProps) {
  const [loading, setIsLoading] = useState(true);
  const [loadingText, setLoadingText] = useState('');
  const [isFailover, setIsFailover] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('closed');
  const [mediaState, dispatch] = useReducer(mediaReducer, mediaShape);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [isSupportGalleryView, setIsSupportGalleryView] = useState<boolean>(false);
  const zmClient = useContext(ZoomContext);
  let webEndpoint = 'zoom.us';
  const mediaContext = useMemo(() => ({ ...mediaState, mediaStream }), [mediaState, mediaStream]);
  const galleryViewWithAttach = window.crossOriginIsolated;
  const identityString: string = localStorage.getItem('zoomIdentity') ?? '';
  const identity = identityString ? JSON.parse(identityString) : {};

  // if (galleryViewWithAttach) {
  //   console.log({
  //     galleryViewWithAttach,
  //     use: '<video-player-container> video tag render video',
  //     doc: 'https://marketplacefront.zoom.us/sdk/custom/web/modules/Stream.html#attachVideo'
  //   });
  // } else {
  //   console.log({
  //     galleryViewWithAttach,
  //     use: '<canvas>',
  //     doc: 'https://marketplacefront.zoom.us/sdk/custom/web/modules/Stream.html#startVideo'
  //   });
  // }

  useEffect(() => {
    const initForDemo = async () => {
      await zmClient.init('en-US', `Global`, {
        webEndpoint,
        stayAwake: true,
        patchJsMedia: true,
        leaveOnPageUnload: false
      });
      // try {
      //   await zmClient.join(topic, signature, name, password).catch((e) => {
      //     console.log(e);
      //   });
      const stream = zmClient.getMediaStream();
      setMediaStream(stream);
      setIsSupportGalleryView(stream.isSupportMultipleVideos());
      setIsLoading(false);
      // } catch (e: any) {
      //   setIsLoading(false);
      //   message.error(e.reason);
      // }
    };
    const init = async () => {
      await zmClient.init('en-US', `Global`, {
        webEndpoint,
        stayAwake: true,
        patchJsMedia: true,
        leaveOnPageUnload: false
      });
      try {
        const stream = zmClient.getMediaStream();
        setMediaStream(stream);
        setIsSupportGalleryView(stream.isSupportMultipleVideos());
        setIsLoading(false);
      } catch (e: any) {
        setIsLoading(false);
        message.error(e.reason);
      }
    };

    checkPermission();
    if (IS_DEMO) {
      initForDemo();
    } else {
      init();
    }
    return () => {
      if (zmClient.getSessionInfo()?.isInMeeting) {
        ZoomVideo.destroyClient();
      }
    };
  }, [zmClient, webEndpoint]);
  const onConnectionChange = useCallback(
    (payload: any) => {
      if (payload.state === ConnectionState.Reconnecting) {
        setIsLoading(true);
        setIsFailover(true);
        setStatus('connecting');
        const { reason, subsessionName } = payload;
        if (reason === ReconnectReason.Failover) {
          setLoadingText('Session Disconnected,Try to reconnect');
        } else if (reason === ReconnectReason.JoinSubsession || reason === ReconnectReason.MoveToSubsession) {
          setLoadingText(`Joining ${subsessionName}...`);
        } else if (reason === ReconnectReason.BackToMainSession) {
          setLoadingText('Returning to Main Session...');
        }
      } else if (payload.state === ConnectionState.Connected) {
        setStatus('connected');
        if (isFailover) {
          setIsLoading(false);
        }

        // FIXME 不要？
        window.zmClient = zmClient;
        window.mediaStream = zmClient.getMediaStream();

        console.log('getSessionInfo', zmClient.getSessionInfo());
      } else if (payload.state === ConnectionState.Closed || payload.state === ConnectionState.Fail) {
        setStatus('closed');
        dispatch({ type: 'reset-media' });
        if (payload.state === ConnectionState.Fail) {
          Modal.error({
            title: 'Join meeting failed',
            content: `Join meeting failed. reason:${payload.reason ?? ''}`
          });
        }
        if (payload.reason === 'ended by host') {
          Modal.warning({
            title: 'Meeting ended',
            content: 'This meeting has been ended by host'
          });
        }
      }
    },
    [isFailover, zmClient]
  );
  const onMediaSDKChange = useCallback((payload: any) => {
    const { action, type, result } = payload;
    dispatch({ type: `${type}-${action}`, payload: result === 'success' });
  }, []);

  const onLeaveOrJoinSession = useCallback(async () => {
    if (status === 'closed') {
      setIsLoading(true);
      // await zmClient.join(topic, signature, name, password);
      setIsLoading(false);
    } else if (status === 'connected') {
      await zmClient.leave();
      message.warn('You have left the session.');
    }
  }, [zmClient, status]);
  useEffect(() => {
    zmClient.on('connection-change', onConnectionChange);
    zmClient.on('media-sdk-change', onMediaSDKChange);
    return () => {
      zmClient.off('connection-change', onConnectionChange);
      zmClient.off('media-sdk-change', onMediaSDKChange);
    };
  }, [zmClient, onConnectionChange, onMediaSDKChange]);

  const checkPermission = () => {
    try {
      const detectedDevice = detectDeviceType();

      if (detectedDevice.os === 'ios') {
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          const cam = devices.find((device) => device.kind === 'videoinput');
          if (cam) {
            navigator.mediaDevices
              .getUserMedia({ video: true })
              .then((stream) => {
                stream.getTracks().forEach((track) => track.stop());
              })
              .catch((error) => {
                console.log('No camera device found ', error);
              });
          } else {
            console.log('No camera device found');
          }
        });
        navigator.mediaDevices
          .enumerateDevices()
          .then((devices) => {
            const audioOutputs = devices.filter((device) => device.kind === 'audiooutput');

            if (audioOutputs.length > 0) {
            } else {
            }
          })
          .catch((error) => {
            console.error('Error checking audio output devices:', error);
          });
      }
    } catch (error) {}
  };
  const { errorState, setErrorState } = useErrorStateContext();

  return (
    <DebugModeProvider>
      <LoggerProvider>
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={() => {
            const roomId = getLocalStorage('roomId');
            const companyId = getLocalStorage('companyId');
            window.location.replace(`/preview/${companyId}/${roomId}`);
          }}
          onError={(error: Error, info: React.ErrorInfo) => {
            console.error(error, info);
          }}
        >
          <UserProvider>
            <LessonProvider>
              <DeviceSettingProvider>
                <div className="App">
                  {ENABLE_DEBUG_MODE && <DebugModeBotton />}
                  {loading && <LoadingLayer content={loadingText} />}
                  {!loading && (
                    <ZoomMediaContext.Provider value={mediaContext}>
                      <ChatProvider>
                        <PiPProvider>
                          <Router>
                            <ErrorDialog dismissError={() => setErrorState(null)} errorState={errorState} />
                            <Switch>
                              {IS_DEMO && (
                                <Route
                                  path="/"
                                  render={(props) => (
                                    <Home {...props} status={status} onLeaveOrJoinSession={onLeaveOrJoinSession} />
                                  )}
                                  exact
                                />
                              )}
                              {IS_DEMO && <Route path="/index.html" component={Home} exact />}
                              <Route
                                path="/video/:companyId/:roomId"
                                component={!galleryViewWithAttach ? Video : VideoAttach}
                              />
                              {IS_DEMO && (
                                <Route path="/video" component={!galleryViewWithAttach ? Video : VideoAttach} />
                              )}
                              {/* <Route path="/subsession" component={Subsession} /> */}
                              <Route path="/preview/:companyId/:roomId" component={Preview} />
                              {IS_DEMO && <Route path="/preview" component={Preview} />}

                              <Route path="*" component={NotFound} />
                            </Switch>
                          </Router>
                        </PiPProvider>
                      </ChatProvider>
                    </ZoomMediaContext.Provider>
                  )}
                </div>
              </DeviceSettingProvider>
            </LessonProvider>
          </UserProvider>
        </ErrorBoundary>
      </LoggerProvider>
    </DebugModeProvider>
  );
}

export default App;
